// Port of Location/LocationTrackingMode.swift: tracking modes, their parameters, and the permission states.
//
// | Mode           | desiredAccuracy (CL)   | distanceFilter | Web: enableHighAccuracy | Web: maximumAge |
// |----------------|------------------------|----------------|-------------------------|-----------------|
// | `off`          | (updates stopped)      | –              | –                       | –               |
// | `batterySaver` | HundredMeters (100)    | 100 m          | false                   | 60 s            |
// | `standard`     | NearestTenMeters (10)  | 25 m           | true                    | 10 s            |
// | `navigation`   | Best (-1)              | 10 m           | true                    | 0               |
// | `recording`    | Best (-1)              | 5 m            | true                    | 0               |
//
// The browser has no desiredAccuracy/distanceFilter: `enableHighAccuracy` asks for GNSS (needed for anything better
// than ~100 m in the mountains, where there is no Wi-Fi), `maximumAge` allows a cached position, and the distance
// filter is applied in code by BrowserLocationProvider.

/** How aggressively the device should track the user's position. */
export type LocationTrackingMode = 'off' | 'batterySaver' | 'standard' | 'navigation' | 'recording';

export const allLocationTrackingModes: readonly LocationTrackingMode[] = [
  'off',
  'batterySaver',
  'standard',
  'navigation',
  'recording',
];

/** CoreLocation accuracy constants (metres; negative values are sentinels), kept for parity with the Swift values. */
export const CLAccuracy = {
  best: -1,
  nearestTenMeters: 10,
  hundredMeters: 100,
  threeKilometers: 3000,
} as const;

export interface TrackingModeParameters {
  /** CoreLocation `desiredAccuracy` of the mode (metres; -1 = best). */
  desiredAccuracy: number;
  /** Minimum horizontal movement (m) before a new fix is delivered. */
  distanceFilter: number;
  /** Whether the mode can keep receiving updates in the background when asked (no effect in a browser). */
  allowsBackground: boolean;
  /** Whether updates may pause automatically when stationary (no effect in a browser). */
  pausesUpdatesAutomatically: boolean;
  /** Whether compass heading updates are requested. */
  usesHeading: boolean;
  /** Whether location updates run at all. */
  isActive: boolean;
  /** `PositionOptions.enableHighAccuracy` for the mode. */
  enableHighAccuracy: boolean;
  /** `PositionOptions.maximumAge` (ms) for the mode. */
  maximumAge: number;
}

export const LocationTrackingModes: Readonly<Record<LocationTrackingMode, TrackingModeParameters>> = {
  off: {
    desiredAccuracy: CLAccuracy.threeKilometers,
    distanceFilter: 1000,
    allowsBackground: false,
    pausesUpdatesAutomatically: true,
    usesHeading: false,
    isActive: false,
    enableHighAccuracy: false,
    maximumAge: Infinity,
  },
  batterySaver: {
    desiredAccuracy: CLAccuracy.hundredMeters,
    distanceFilter: 100,
    allowsBackground: true,
    pausesUpdatesAutomatically: true,
    usesHeading: false,
    isActive: true,
    enableHighAccuracy: false,
    maximumAge: 60_000,
  },
  standard: {
    desiredAccuracy: CLAccuracy.nearestTenMeters,
    distanceFilter: 25,
    allowsBackground: true,
    pausesUpdatesAutomatically: true,
    usesHeading: false,
    isActive: true,
    enableHighAccuracy: true,
    maximumAge: 10_000,
  },
  navigation: {
    desiredAccuracy: CLAccuracy.best,
    distanceFilter: 10,
    allowsBackground: true,
    pausesUpdatesAutomatically: false,
    usesHeading: true,
    isActive: true,
    enableHighAccuracy: true,
    maximumAge: 0,
  },
  recording: {
    desiredAccuracy: CLAccuracy.best,
    distanceFilter: 5,
    allowsBackground: true,
    pausesUpdatesAutomatically: false,
    usesHeading: false,
    isActive: true,
    enableHighAccuracy: true,
    maximumAge: 0,
  },
};

/** Heading filter used while heading updates are active, degrees. */
export const headingFilterDegrees = 5;

/** `watchPosition` options for a mode (no timeout: a slow first fix in a valley is not an error). */
export function geolocationOptions(mode: LocationTrackingMode): PositionOptions {
  const p = LocationTrackingModes[mode];
  return { enableHighAccuracy: p.enableHighAccuracy, maximumAge: p.maximumAge };
}

/**
 * App-level view of the location permission. `unknown` until the browser has reported it (Permissions API answer,
 * or the outcome of a prompt where that API is missing): nothing is decided on it.
 */
export type LocationAuthorization = 'unknown' | 'notDetermined' | 'denied' | 'restricted' | 'whenInUse' | 'always';

/** When In Use or Always. */
export function isAuthorizationGranted(a: LocationAuthorization): boolean {
  return a === 'whenInUse' || a === 'always';
}

/** Denied or restricted: location is off for TrailBivvy until the user changes it in the browser settings. */
export function isAuthorizationOff(a: LocationAuthorization): boolean {
  return a === 'denied' || a === 'restricted';
}

/** The permission has been reported (anything but `unknown`). */
export function isAuthorizationKnown(a: LocationAuthorization): boolean {
  return a !== 'unknown';
}

/** Whether location updates can be started. */
export function permitsUpdates(a: LocationAuthorization): boolean {
  return isAuthorizationGranted(a);
}

/** Browser permission state → authorization (`granted` is When In Use: a web page never gets "Always"). */
export function authorizationFromPermissionState(state: PermissionState): LocationAuthorization {
  switch (state) {
    case 'granted':
      return 'whenInUse';
    case 'denied':
      return 'denied';
    case 'prompt':
      return 'notDetermined';
    default:
      // Conservative: an unknown future state never starts tracking.
      return 'denied';
  }
}

/** Whether positions are precise. `reduced` until the permission is known to be granted. */
export type LocationAccuracyAuthorization = 'full' | 'reduced';
